import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { H2, Loading } from "commons";
import { IN_PROGRESS, isFailure, isInProgress } from "commons/src/helpers/RequestHelpers";

import { AccountBlock } from "./AccountBlock";
import { MemoAssetsBlock } from "./MemoAssetsBlock";
import { PortfolioTotalBlock } from "./PortfolioTotalBlock";
import { requestObject } from "../../reducers/requestsActions";

const Accounts = ({ list }) =>
    list.map(acc => (
        <AccountBlock
            key={acc.accountNumber}
            name={acc.wrapperType}
            accountNumber={acc.accountNumber}
            status={acc.accountStatus}
            value={acc.totalHoldings}
            isJoint={acc.numberOfOwners > 1}
            alias={acc.alias}
        />
    ));

export function Home({
    accounts = {},
    analysis,
    memoAssets = [],
    accountsRequestStatus = { status: IN_PROGRESS },
    memoAssetsRequestStatus = { status: IN_PROGRESS },
    portfolioPerformanceRequestStatus = { status: IN_PROGRESS }
}) {
    const calculatePortfolioTotal = accountsValues =>
        accountsValues.reduce((sum, value) => sum + value.totalHoldings, 0);

    const hasValidMemoAssets = memoAssets =>
        memoAssets.some(memoAsset => memoAsset.approximateValue);

    const calculateMemoAssetsTotal = memoAssets =>
        memoAssets.reduce((sum, value) => sum + (value.approximateValue || 0), 0);

    const accountsValues = Object.values(accounts).filter(acc => acc.accountNumber !== "Combined");
    const portfolioTotal = calculatePortfolioTotal(accountsValues);

    const activeAccounts = accountsValues.filter(acc => acc.accountStatus !== "Closed");
    const closedAccounts = accountsValues.filter(acc => acc.accountStatus === "Closed");
    const hasActiveAccounts = activeAccounts.length > 0;
    const hasClosedAccounts = closedAccounts.length > 0;

    return (
        <Container>
            <PortfolioTotalBlock
                value={portfolioTotal}
                accounts={accounts}
                analysis={analysis}
                requestStatus={portfolioPerformanceRequestStatus}
            />

            {isInProgress(accountsRequestStatus.status) ? (
                <AccountsHeader secondary margin="2rem 0 0 0">
                    My accounts
                </AccountsHeader>
            ) : null}

            <Loading
                displayWhen={Object.entries(accounts).length !== 0}
                className={"spinner-big"}
                size={35}
            >
                {hasActiveAccounts ? (
                    <>
                        <AccountsHeader secondary margin="2rem 0 0 0">
                            My accounts
                        </AccountsHeader>
                        <AccountsContainer>
                            <Accounts list={activeAccounts} />
                        </AccountsContainer>
                    </>
                ) : null}

                {hasClosedAccounts ? (
                    <>
                        <AccountsHeader secondary margin={hasActiveAccounts ? "0" : "2rem 0 0 0"}>
                            My closed accounts
                        </AccountsHeader>
                        <AccountsContainer>
                            <Accounts list={closedAccounts} />
                        </AccountsContainer>
                    </>
                ) : null}

                {(hasValidMemoAssets(memoAssets) || isFailure(memoAssetsRequestStatus.status)) && (
                    <AccountsContainer>
                        <MemoAssetsBlock
                            value={calculateMemoAssetsTotal(memoAssets)}
                            requestStatus={memoAssetsRequestStatus}
                        />
                    </AccountsContainer>
                )}
            </Loading>
        </Container>
    );
}

const AccountsHeader = styled(H2)`
    @media (max-width: ${props => props.theme.maxWidth}) {
        font-size: 1.28em;
    }
`;

const AccountsContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 2rem;

    @media (max-width: ${props => props.theme.maxWidth}) {
        flex-direction: column;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

Home.propTypes = {
    accounts: PropTypes.object,
    analysis: PropTypes.object,
    memoAssets: PropTypes.array,
    accountsRequestStatus: requestObject,
    memoAssetsRequestStatus: requestObject,
    portfolioPerformanceRequestStatus: requestObject
};
